import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-economics-exam-first-5-minutes",
  title: "The First 5 Minutes of a DU Economics Exam: Tactical Hall Timetable & Selection Strategy",
  description:
    "How to scan the paper, select optional questions, map time per mark, and execute a 180-minute exam hall strategy for 75-mark and 90-mark DU end-semester papers.",
  pubDate: "2026-05-30",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "exam-strategy",
  tags: ["exam-strategy", "economics", "du-exams", "time-management", "exam-hall-tactics"],
  readMinutes: 11,
  heroEyebrow: "Exam Craft · Tactics Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        The first 5 minutes after receiving your end-semester question paper set by the{" "}
        <a href="https://exam.du.ac.in/" target="_blank" rel="noopener noreferrer">
          DU Examination Branch
        </a>{" "}
        dictate your performance over the next three hours. Entering an exam hall without a precise minute-by-minute timetable leads to rushed, incomplete final answers and lost distinction marks.
      </p>

      <figure className="my-8">
        <img
          src="/blog/inline-ui-2.jpg"
          alt="Diagrammatic timeline showing exam hall time allocation across 180 minutes."
          width={1200}
          height={675}
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Figure 1: Exam Hall 180-Minute Master Timeline. Allocate 5 minutes for scanning, 35 minutes per 15-mark question, and reserve a 5-minute audit buffer at the end.
        </figcaption>
      </figure>

      <h2>Minute-by-Minute Hall Timetable (180 Minutes Total)</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Time Window</th>
              <th className="p-3 border-r border-rule">Phase &amp; Action</th>
              <th className="p-3">Tactical Objective</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">0:00 – 0:05 (5 mins)</td>
              <td className="p-3 border-r border-rule">Scanning &amp; Question Selection</td>
              <td className="p-3">Read all optional choices. Select 5 questions with highest quantitative certainty. Pencil-mark chosen questions.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">0:05 – 0:40 (35 mins)</td>
              <td className="p-3 border-r border-rule">Question 1 (Strongest Numerical)</td>
              <td className="p-3">Solve your most confident 15-mark numerical problem first to bank high marks early.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">0:40 – 1:15 (35 mins)</td>
              <td className="p-3 border-r border-rule">Question 2 (Second Numerical)</td>
              <td className="p-3">Execute second quantitative question. Check Bordered Hessian / FOC conditions before moving on.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">1:15 – 1:50 (35 mins)</td>
              <td className="p-3 border-r border-rule">Question 3 (Major Theory Model)</td>
              <td className="p-3">Write 5-page structured long answer (IS-LM or Solow Model) with full diagrams.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">1:50 – 2:25 (35 mins)</td>
              <td className="p-3 border-r border-rule">Question 4 (Second Major Theory)</td>
              <td className="p-3">Write 4-to-5 page theoretical answer with explicit assumptions and critical evaluation.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">2:25 – 2:55 (30 mins)</td>
              <td className="p-3 border-r border-rule">Question 5 (Short Notes / Final Q)</td>
              <td className="p-3">Complete short notes (7.5 marks x 2). Use crisp bullet points and focused diagrams.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">2:55 – 3:00 (5 mins)</td>
              <td className="p-3 border-r border-rule">Final Audit &amp; Verification</td>
              <td className="p-3">Verify roll number, tie main sheet tags, check numerical units, and confirm question numbering.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>The Question Selection Decision Matrix</h2>
      <p>
        During the initial 5-minute scan, score each optional question out of 10 based on two criteria:
      </p>
      <ul>
        <li><strong>Quantitative Certainty (Weight 60%):</strong> Does the question contain a numerical or mathematical proof where full marks can be guaranteed?</li>
        <li><strong>Diagrammatic Mastery (Weight 40%):</strong> Do you remember the exact axis labels, curve shifts, and parameter assumptions for the accompanying economic diagram?</li>
      </ul>

      <h2>Related Tactical Guides</h2>
      <p>
        Learn how to structure your selected 15-mark responses by reading{" "}
        <a href="/blog/du-long-answer-question-playbook" className="font-semibold underline text-grade">
          The DU Long-Answer Question Playbook
        </a>{" "}
        and avoid calculation errors using{" "}
        <a href="/blog/where-students-lose-marks-numericals" className="font-semibold underline text-grade">
          Where DU Students Lose Marks in Numericals
        </a>
        .
      </p>
    </>
  );
}


